import { axiosInstance } from "@/lib/axios-instance";

/**
 * Website traffic estimates for a deal, sourced from a third-party provider.
 *
 * Coverage is patchy and not correlated with company size, so every metric is
 * nullable and "no_data" is a normal outcome rather than an error.
 */
export type WebsiteAnalyticsState =
  | "disabled"
  | "no_website"
  | "not_yet_fetched"
  | "no_data"
  | "ok"
  | "error";

export interface WebsiteAnalyticsSeriesPoint {
  month: string; // "YYYY-MM"
  visits: number;
}

export interface WebsiteAnalyticsMetrics {
  total_website_visits_monthly: number | null;
  change_monthly: number | null;
  change_monthly_percentage: number | null;
  change_quarterly: number | null;
  change_quarterly_percentage: number | null;
  change_yearly: number | null;
  change_yearly_percentage: number | null;
  /** Provider scale is 0-1, not 0-100. */
  bounce_rate: number | null;
  pages_per_visit: number | null;
  average_visit_duration_seconds: number | null;
  rank_global: number | null;
  rank_country: number | null;
}

export interface WebsiteAnalyticsCoverage {
  first_month: string | null;
  last_month: string | null;
  months_covered: number;
  months_missing: number;
}

export interface WebsiteAnalyticsData {
  state: WebsiteAnalyticsState;
  enabled: boolean;
  domain: string | null;
  /**
   * Only sent while disabled. False means enabling would 409, so the card
   * offers to make the deal premium first rather than failing the click.
   */
  is_premium?: boolean;
  fetch_status?: "ok" | "no_data" | "error";
  no_data_reason?: string | null;
  last_fetched_at?: string | null;
  last_success_at?: string | null;
  metrics?: WebsiteAnalyticsMetrics;
  /** Ascending. Months the provider never published are absent, not zero. */
  monthly_series?: WebsiteAnalyticsSeriesPoint[];
  coverage?: WebsiteAnalyticsCoverage;
}

export interface GetWebsiteAnalyticsResponse {
  status: string;
  message?: string;
  data: WebsiteAnalyticsData | null;
}

export interface EnableWebsiteAnalyticsResponse {
  status: string;
  message?: string;
  code?: string;
  data?: { enabled: boolean; domain: string; state: WebsiteAnalyticsState };
}

export const websiteAnalyticsApi = {
  get: async (
    opportunityId: string,
    companyId: string
  ): Promise<GetWebsiteAnalyticsResponse> => {
    const response = await axiosInstance.get<GetWebsiteAnalyticsResponse>(
      "/get_website_analytics",
      { params: { opportunity_id: opportunityId, company_id: companyId } }
    );
    return response.data;
  },

  enable: async (
    opportunityId: string,
    companyId: string
  ): Promise<EnableWebsiteAnalyticsResponse> => {
    const response = await axiosInstance.post<EnableWebsiteAnalyticsResponse>(
      "/enable_website_analytics",
      { opportunity_id: opportunityId, company_id: companyId }
    );
    return response.data;
  },

  disable: async (
    opportunityId: string,
    companyId: string
  ): Promise<EnableWebsiteAnalyticsResponse> => {
    const response = await axiosInstance.post<EnableWebsiteAnalyticsResponse>(
      "/disable_website_analytics",
      { opportunity_id: opportunityId, company_id: companyId }
    );
    return response.data;
  },
};
